import { AnimatePresence, motion, type Variants } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { useCallback, useState } from 'react';

import { useAutoDetectMeetingsPreference } from '../../hooks/use-auto-detect-meetings-preference';
import { useCalendarNotificationPreference } from '../../hooks/use-calendar-notification-preference';
import * as styles from './permissions-dock.css';

interface Permission {
  id: string;
  label: string;
  description: string;
}

export const PermissionsDockModal = ({ onClose }: { onClose: () => void }) => {
  const calendarNotification = useCalendarNotificationPreference();
  const autoDetectMeetings = useAutoDetectMeetingsPreference();
  const [isClosing, setIsClosing] = useState(false);

  const permissions: Permission[] = [
    {
      id: 'notifications',
      label: 'Calendar Event Notifications',
      description: 'Receive alerts for upcoming calendar meetings',
    },
    {
      id: 'auto-detect',
      label: 'Auto-Detect Meetings',
      description: 'Get notified when joining Zoom, Teams, or other meetings',
    },
  ];

  const isChecked = useCallback(
    (id: string): boolean => {
      if (id === 'auto-detect') return autoDetectMeetings.enabled;
      if (id === 'notifications') return calendarNotification.enabled;
      return false;
    },
    [autoDetectMeetings.enabled, calendarNotification.enabled]
  );

  const handleToggle = useCallback(
    async (id: string) => {
      if (id === 'auto-detect') {
        await autoDetectMeetings.toggle();
      } else if (id === 'notifications') {
        await calendarNotification.toggle();
      }
    },
    [autoDetectMeetings, calendarNotification]
  );

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 250);
  }, [onClose]);

  const overlayVariants: Variants = {
    visible: {
      opacity: 1,
      transition: { duration: 0.2, ease: 'easeOut' },
    },
    hidden: {
      opacity: 0,
      transition: { duration: 0.2, ease: 'easeIn' },
    },
  };

  const containerVariants: Variants = {
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] },
    },
    hidden: {
      y: 20,
      opacity: 0,
      scale: 0.98,
      transition: { duration: 0.2, ease: [0.4, 0, 1, 1] },
    },
  };

  return (
    <AnimatePresence>
      <motion.div
        className={styles.overlay}
        variants={overlayVariants}
        initial="hidden"
        animate={isClosing ? 'hidden' : 'visible'}
        onClick={handleClose}
      >
        <motion.div
          className={styles.modalContainer}
          variants={containerVariants}
          initial="hidden"
          animate={isClosing ? 'hidden' : 'visible'}
          onClick={e => e.stopPropagation()}
        >
          <button className={styles.closeButton} onClick={handleClose}>
            <X size={16} />
          </button>

          <div className={styles.header}>
            <h3 className={styles.title}>Notification Preferences</h3>
          </div>

          <div className={styles.permissionsContainer}>
            {permissions.map(permission => {
              const checked = isChecked(permission.id);

              return (
                <div
                  key={permission.id}
                  className={styles.permissionItem}
                  data-checked={checked}
                  onClick={() => handleToggle(permission.id)}
                >
                  <div className={styles.checkbox} data-checked={checked}>
                    {checked && (
                      <Check className={styles.checkmark} strokeWidth={3} />
                    )}
                  </div>
                  <div className={styles.permissionContent}>
                    <span className={styles.permissionLabel}>
                      {permission.label}
                    </span>
                    <span className={styles.permissionDescription}>
                      {permission.description}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <p className={styles.footer}>Recommended for the full experience</p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
